import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Stamp, Download, Trash2, ShieldCheck, ShieldAlert, RefreshCw,
  ChevronLeft, Copy, ExternalLink, Share2, Ban,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * OneStamp Library — cryptographic prior-art proofs.
 *
 * Every MindStein bot response is auto-stamped (SHA-256 + HMAC-SHA256 +
 * UTC timestamp). This page lets the user list, verify, download the
 * PDF certificate, or delete a stamp.
 */
export default function OneStampPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [roadmap, setRoadmap] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/onestamp/roadmap`);
        setRoadmap(r.data);
      } catch { /* non-fatal */ }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr("");
      try {
        const r = await axios.get(`${API}/api/onestamp/list`,
          { withCredentials: true, params: { limit: 100 } });
        if (cancelled) return;
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
      } catch (e) {
        if (cancelled) return;
        setErr(e?.response?.data?.detail || e?.message || "Unable to load stamps.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshTick]);

  const openStamp = async (stampId) => {
    setSelected(null); setVerifyResult(null);
    try {
      const r = await axios.get(`${API}/api/onestamp/${stampId}`, { withCredentials: true });
      setSelected(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to open stamp.");
    }
  };

  const verifyStamp = async (stampId) => {
    setVerifying(true); setVerifyResult(null);
    try {
      const r = await axios.get(`${API}/api/onestamp/${stampId}/verify`, { withCredentials: true });
      setVerifyResult(r.data);
      if (r.data.valid) toast.success("Signature verified — stamp is intact.");
      else toast.error("Verification failed — signature/hash mismatch.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed.");
    } finally {
      setVerifying(false);
    }
  };

  const downloadPdf = (stampId) => {
    const url = `${API}/api/onestamp/${stampId}/pdf`;
    window.open(url, "_blank", "noopener");
  };

  const deleteStamp = async (stampId) => {
    if (!window.confirm("Delete this stamp permanently? This cannot be undone."))
      return;
    try {
      await axios.delete(`${API}/api/onestamp/${stampId}`, { withCredentials: true });
      toast.success("Stamp deleted.");
      setSelected(null);
      setRefreshTick(t => t + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to delete stamp.");
    }
  };

  const copyHash = (h) => {
    navigator.clipboard.writeText(h);
    toast.success("Hash copied to clipboard.");
  };

  const publicShareUrl = (code) =>
    code ? `${window.location.origin}/onestamp/verify/${code}` : "";

  const shareStamp = async () => {
    if (!selected) return;
    try {
      const r = await axios.post(
        `${API}/api/onestamp/${selected.stamp_id}/share`, {},
        { withCredentials: true },
      );
      const updated = { ...selected, share_code: r.data.share_code,
                        share_created_at: r.data.share_created_at };
      setSelected(updated);
      const url = publicShareUrl(r.data.share_code);
      navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Public verification link created — copied to clipboard.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to create share link.");
    }
  };

  const revokeShare = async () => {
    if (!selected?.share_code) return;
    if (!window.confirm("Revoke this public verification link? The URL will stop working immediately."))
      return;
    try {
      await axios.delete(`${API}/api/onestamp/${selected.stamp_id}/share`,
        { withCredentials: true });
      const { share_code, share_created_at, ...rest } = selected;
      setSelected(rest);
      toast.success("Public link revoked.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to revoke.");
    }
  };

  const copyShareUrl = () => {
    if (!selected?.share_code) return;
    const url = publicShareUrl(selected.share_code);
    navigator.clipboard.writeText(url);
    toast.success("Public link copied.");
  };

  const shortId = (id) => (id ? `${id.slice(0, 6)}…${id.slice(-4)}` : "");

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100 font-mono">
      <header className="border-b border-[#27272A] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" data-testid="onestamp-back-dashboard"
                className="text-zinc-400 hover:text-white flex items-center gap-1">
            <ChevronLeft size={16}/> DASHBOARD
          </Link>
          <span className="text-zinc-600">/</span>
          <div className="flex items-center gap-2">
            <Stamp size={18} className="text-amber-400" />
            <span className="text-[13px] uppercase tracking-[0.2em]">ONESTAMP_LIBRARY</span>
          </div>
        </div>
        <button onClick={() => setRefreshTick(t => t + 1)}
                data-testid="onestamp-refresh"
                className="text-[11px] uppercase tracking-[0.2em] flex items-center gap-1 text-zinc-400 hover:text-white">
          <RefreshCw size={14}/> REFRESH
        </button>
      </header>

      <main className="p-6 max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(360px,540px)] gap-6">
        {/* ── LEFT: list ─────────────────────────────────────────── */}
        <section>
          <div className="mb-4 border border-amber-500/30 bg-amber-500/[0.03] p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="text-amber-400 shrink-0 mt-0.5" size={20}/>
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-amber-300">
                  AUTO_STAMPED · PRIOR_ART_PROOF
                </div>
                <p className="mt-1 text-[13px] text-zinc-300 leading-relaxed">
                  Every MindStein chat response is instantly hashed (SHA-256),
                  timestamped in UTC, and signed with a server key —
                  cryptographic proof the work existed at that moment.
                  Download the PDF certificate to share with a lawyer, VC, or
                  patent office. Delete anything you&apos;d rather not keep on record.
                </p>
                {roadmap && (
                  <div className="mt-2 text-[11px] text-zinc-500">
                    Current: <span className="text-zinc-300">{roadmap.current?.algorithm} · v{roadmap.current?.version}</span>.
                    Coming before launch: <span className="text-amber-300">Ed25519 public-key + RFC 3161 timestamp authority</span>.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">
              <span data-testid="onestamp-total">{total}</span> stamps · showing {items.length}
            </div>
          </div>

          {loading && (
            <div className="text-[12px] text-zinc-500" data-testid="onestamp-loading">Loading…</div>
          )}
          {err && (
            <div className="border border-rose-500/40 bg-rose-500/10 p-3 text-[12px] text-rose-300"
                 data-testid="onestamp-error">{err}</div>
          )}
          {!loading && !err && items.length === 0 && (
            <div className="border border-dashed border-[#27272A] p-8 text-center text-zinc-500 text-[13px]"
                 data-testid="onestamp-empty">
              No stamps yet. Send a message from the{" "}
              <Link to="/bot" className="text-amber-300 hover:underline">MindStein bot</Link>{" "}
              and it&apos;ll appear here instantly.
            </div>
          )}

          <ul className="space-y-2" data-testid="onestamp-list">
            {items.map((s) => (
              <li key={s.stamp_id}
                  data-testid={`onestamp-row-${s.stamp_id}`}
                  onClick={() => openStamp(s.stamp_id)}
                  className={`border p-3 cursor-pointer transition-colors ${
                    selected?.stamp_id === s.stamp_id
                      ? "border-amber-400/60 bg-amber-500/10"
                      : "border-[#27272A] hover:border-amber-500/40 hover:bg-amber-500/5"}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-amber-300">
                    STAMP · {shortId(s.stamp_id)}
                  </div>
                  <div className="text-[10px] text-zinc-500">{s.created_at}</div>
                </div>
                <div className="mt-1 text-[12px] text-zinc-300 line-clamp-2">
                  {s.preview || "(no preview)"}
                </div>
                <div className="mt-2 text-[10px] font-mono text-zinc-500 truncate">
                  sha256: {s.content_hash}
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* ── RIGHT: detail panel ────────────────────────────────── */}
        <aside className="lg:sticky lg:top-4 h-fit">
          {!selected && (
            <div className="border border-[#27272A] p-6 text-center text-zinc-500 text-[12px]"
                 data-testid="onestamp-detail-empty">
              Select a stamp on the left to view details, verify, download, or delete.
            </div>
          )}
          {selected && (
            <div className="border border-[#27272A] bg-[#0B0B0B] p-5" data-testid="onestamp-detail">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-[0.2em] text-amber-300">STAMP_DETAIL</div>
                <div className="flex gap-2">
                  <button data-testid="onestamp-verify"
                          onClick={() => verifyStamp(selected.stamp_id)}
                          disabled={verifying}
                          className="text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50">
                    {verifying ? "VERIFYING…" : "VERIFY"}
                  </button>
                  <button data-testid="onestamp-download-pdf"
                          onClick={() => downloadPdf(selected.stamp_id)}
                          className="text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 flex items-center gap-1">
                    <Download size={12}/> PDF
                  </button>
                  <button data-testid="onestamp-delete"
                          onClick={() => deleteStamp(selected.stamp_id)}
                          className="text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20 flex items-center gap-1">
                    <Trash2 size={12}/> DELETE
                  </button>
                </div>
              </div>

              <dl className="mt-4 grid grid-cols-[110px_1fr] gap-y-2 text-[11px]">
                <dt className="text-zinc-500">stamp_id</dt>
                <dd className="text-zinc-200 break-all">{selected.stamp_id}</dd>
                <dt className="text-zinc-500">algo · ver</dt>
                <dd className="text-zinc-200">{selected.algo} · v{selected.version}</dd>
                <dt className="text-zinc-500">signed_at</dt>
                <dd className="text-zinc-200">{selected.created_at}</dd>
                <dt className="text-zinc-500">session</dt>
                <dd className="text-zinc-400">{selected.session_id || "-"}</dd>
              </dl>

              <div className="mt-4">
                <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-1">
                  <span>SHA-256 CONTENT HASH</span>
                  <button onClick={() => copyHash(selected.content_hash)}
                          data-testid="onestamp-copy-hash"
                          className="hover:text-zinc-200 flex items-center gap-1">
                    <Copy size={11}/> COPY
                  </button>
                </div>
                <div className="border border-[#27272A] bg-black/40 p-2 text-[10px] font-mono break-all">
                  {selected.content_hash}
                </div>
              </div>

              <div className="mt-3">
                <div className="text-[10px] text-zinc-500 mb-1">HMAC-SHA256 SIGNATURE</div>
                <div className="border border-[#27272A] bg-black/40 p-2 text-[10px] font-mono break-all">
                  {selected.signature}
                </div>
              </div>

              {/* Public share flow */}
              <div className="mt-4 border border-fuchsia-500/30 bg-fuchsia-500/[0.04] p-3"
                   data-testid="onestamp-share-block">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-fuchsia-300">
                    PUBLIC_SHARE · REVOCABLE
                  </div>
                  {selected.share_code ? (
                    <button data-testid="onestamp-share-revoke" onClick={revokeShare}
                            className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20 flex items-center gap-1">
                      <Ban size={11}/> REVOKE
                    </button>
                  ) : (
                    <button data-testid="onestamp-share-create" onClick={shareStamp}
                            className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-200 hover:bg-fuchsia-500/20 flex items-center gap-1">
                      <Share2 size={11}/> CREATE_LINK
                    </button>
                  )}
                </div>
                {selected.share_code ? (
                  <div className="mt-2">
                    <div className="text-[10px] text-zinc-500">Public URL</div>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        readOnly
                        value={publicShareUrl(selected.share_code)}
                        data-testid="onestamp-share-url"
                        onFocus={(e) => e.target.select()}
                        className="flex-1 bg-black/40 border border-[#27272A] px-2 py-1 text-[10px] font-mono text-zinc-200"
                      />
                      <button data-testid="onestamp-share-copy" onClick={copyShareUrl}
                              className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-zinc-600 hover:bg-zinc-900 flex items-center gap-1">
                        <Copy size={11}/> COPY
                      </button>
                    </div>
                    <div className="mt-1 text-[10px] text-zinc-500">
                      Anyone with this link can verify hash + signature but NOT read the
                      snapshot. Revoke anytime.
                    </div>
                  </div>
                ) : (
                  <div className="mt-1 text-[10px] text-zinc-500 leading-relaxed">
                    Generate a short, revocable URL so a lawyer, VC, or partner can
                    verify this stamp in-browser without you handing over the PDF or
                    logging into your account.
                  </div>
                )}
              </div>

              {verifyResult && (
                <div className={`mt-4 p-3 border text-[12px] flex items-start gap-2 ${
                  verifyResult.valid
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                    : "border-rose-500/40 bg-rose-500/10 text-rose-200"}`}
                     data-testid="onestamp-verify-result">
                  {verifyResult.valid
                    ? <ShieldCheck size={18} className="shrink-0 mt-0.5"/>
                    : <ShieldAlert size={18} className="shrink-0 mt-0.5"/>}
                  <div>
                    <div className="uppercase tracking-[0.2em] text-[10px]">
                      {verifyResult.valid ? "VERIFICATION_PASSED" : "VERIFICATION_FAILED"}
                    </div>
                    <div className="text-[11px] mt-0.5 opacity-80">
                      hash_matches={String(verifyResult.hash_matches)} ·
                      signature_valid={String(verifyResult.signature_valid)}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-4 text-[10px] text-zinc-500 leading-relaxed">
                Prompt + response snapshot is embedded inside the PDF. Delete this
                stamp if you&apos;d rather not keep the record. Every stamp is scoped
                to your account — nobody else can view or verify it.
              </div>
            </div>
          )}
        </aside>
      </main>
    </div>
  );
}
